import React from "react";
import { useState, useRef, useEffect } from "react";
import {
    Box, Typography, Chip, Button, IconButton, Grid, TextField, Stack, Popper, Grow, Paper, ClickAwayListener, Tabs, Tab,
    Menu, MenuItem
} from "@mui/material";
import moment from "moment";
import TableComponent from "components/grid";
import StatusBadge from "components/badge/StatusBadge";
import CustomPagination from "components/CustomPagination";
import { ReactComponent as EditIcon } from "assets/icons/EditIcon.svg";
import { LuEye, LuRefreshCw, LuSearch } from "react-icons/lu";
import { MdOutlineFilterAlt } from "react-icons/md";
import { FaPlus } from "react-icons/fa6";
import Select from "react-select";
import CustomButton from "components/Button";
import AddInvoice from "./AddInvoice";
import EditInvoice from "./EditInvoice";
import ViewInvoice from "./ViewInvoice";
import { useLazyGetInvoiceListQuery, useLazyGetExportInvoiceListQuery } from "store/slices/apis/invoiceApiSlice";
import { useParams } from "react-router-dom";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import CalenderSVG from "assets/logo/Calendar";
import { useGetMasterDataQuery } from "store/slices/apis/masterConfigation";
import { mapAndSortAlphabetically } from "utils/mapSortHelper";
import { usePermission } from "hooks/usePermission";
import { INVOICE_TRACKER_CREATE, INVOICE_TRACKER_EDIT, INVOICE_TRACKER_VIEW } from "helpers/permissionConfig";
import { useGetLastUpdatedOnStatusQuery } from "store/slices/apis/projectStatusApiSlice";
import ProjectLastUpdatedOn from "components/ProjectLastUpdatedOn";
import TrackerPageHeader from "components/TrackerPageHeader";
import { MdOutlineFileDownload } from "react-icons/md";
import { DEFAULT_DATE_FORMAT } from "config";
import useExcelExport from "hooks/useExcelExport";

const totalCount = 0;
const openCount = 0;
const closedCount = 0;

const InvoiceTracker = () => {

    const [menuAnchorEl, setMenuAnchorEl] = useState(null);
    const [menuRow, setMenuRow] = useState(null);

    const [searchTerm, setSearchTerm] = useState("");

    const handleSearchChange = (e) => {
        setSearchTerm(e.target.value);
    };

    const handleMenuOpen = (event, row) => {
        event.stopPropagation(); // prevent row click if any
        setMenuAnchorEl(event.currentTarget);
        setMenuRow(row);
    };

    const handleMenuClose = () => {
        setMenuAnchorEl(null);
        setMenuRow(null);
    };

    const handleMenuView = () => {
        if (menuRow) {
            handleViewClick(menuRow);
        }
        handleMenuClose();
    };

    const handleMenuEdit = () => {
        if (menuRow) {
            handleEditInvoice(menuRow);
        }
        handleMenuClose();
    };
    const { projectId } = useParams();

    const [pageNumber, setPageNumber] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [sortOrder, setSortOrder] = useState('desc');
    const [sortBy, setSortBy] = useState('CreatedDate');

    const [open, setOpen] = useState(false);
    const [tabValue, setTabValue] = useState(0);
    const anchorRef = useRef(null);
    const [isAddInvoice, setIsAddInvoice] = useState(false);
    const [isEdit, setIsEdit] = useState(false);
    const [selectedData, setSelectedData] = useState(null);
    const [isView, setIsView] = useState(false);
    const [viewData, setViewData] = useState(null);

    const [filters, setFilters] = useState({
        SortBy: sortBy,
        SortOrder: sortOrder,
        PageNumber: pageNumber,
        PageSize: pageSize,
        ProjectId: projectId,
        SearchTerm: "",
    });

    const createAccess = usePermission(INVOICE_TRACKER_CREATE);
    const editAccess = usePermission(INVOICE_TRACKER_EDIT);
    const viewAccess = usePermission(INVOICE_TRACKER_VIEW);

    const module = "Procurement";
    const { data: vendorNameList } = useGetMasterDataQuery({ module: module, category: 'VendorName', ProjectId: projectId });
    const vendorNameData = vendorNameList?.data || [];
    const vendorNameOptions = mapAndSortAlphabetically(vendorNameData);

    const module1 = "Invoice";
    const { data: statusList } = useGetMasterDataQuery({ module: module1, category: 'Status', ProjectId: projectId });
    const statusData = statusList?.data || [];
    const statusOptions = mapAndSortAlphabetically(statusData);

    const [trigger, { data: invoiceList, isFetching }] = useLazyGetInvoiceListQuery();
    const { data: invoiceData } = useGetLastUpdatedOnStatusQuery({ ProjectId: projectId });

    const { handleExport, isExporting } = useExcelExport({ exportHook: useLazyGetExportInvoiceListQuery, fileName: "Invoice_Tracker_Data" });

    const exportInvoiceData = (rows) =>
        rows.map((row, index) => {

            const comments = row?.comments;

            let latestComment = "-";

            if (comments && comments.length > 0) {
                const sortedComments = [...comments].sort(
                    (a, b) =>
                        new Date(b.createdDate) -
                        new Date(a.createdDate)
                );

                latestComment =
                    sortedComments[0]?.comments || "-";
            }
            
            return {
                "S.No": index + 1,
                "ID": row.invoiceUniqueId || "-",
                "Vendor Name": row.vendorName || "-",
                "WO/Contract No.": row.woContractNo || "-",
                "Invoice Particulars": row.invoiceParticulars || "-",
                "Type": row.type || "-",
                "Invoice No.": row.invoiceNo || "-",
                "Invoice Date": row.invoiceDate && moment(row.invoiceDate).isValid()
                    ? moment(row.invoiceDate).format(DEFAULT_DATE_FORMAT)
                    : '-',
                "Total Invoice Amount (Inc GST)": Number(row.totalInvoiceAmountIncGst).toLocaleString("en-IN") || "-",
                "Received From Vendor": row.receivedFromVendor && moment(row.receivedFromVendor).isValid()
                    ? moment(row.receivedFromVendor).format(DEFAULT_DATE_FORMAT)
                    : '-',
                "Invoice Received For PMO Review": row.invoiceReceivedForPmoReview && moment(row.invoiceReceivedForPmoReview).isValid()
                    ? moment(row.invoiceReceivedForPmoReview).format(DEFAULT_DATE_FORMAT)
                    : '-',
                "PMO Review": row.pmoReview && moment(row.pmoReview).isValid()
                    ? moment(row.pmoReview).format(DEFAULT_DATE_FORMAT)
                    : '-',
                "Comments": latestComment,
                "PMO Review After Comments Incorporated": row.pmoReviewAfterCommentsIncorporated && moment(row.pmoReviewAfterCommentsIncorporated).isValid()
                    ? moment(row.pmoReviewAfterCommentsIncorporated).format(DEFAULT_DATE_FORMAT)
                    : '-',
                "Sent To Approver 1": row.sentToApprover1 && moment(row.sentToApprover1).isValid()
                    ? moment(row.sentToApprover1).format(DEFAULT_DATE_FORMAT)
                    : '-',
                "Approver 1": row.approver1 || "-",
                "Approver 1 Approval Date": row.approver1ApprovalDate && moment(row.approver1ApprovalDate).isValid()
                    ? moment(row.approver1ApprovalDate).format(DEFAULT_DATE_FORMAT)
                    : '-',
                "Sent To Approver 2": row.sentToApprover2 && moment(row.sentToApprover2).isValid()
                    ? moment(row.sentToApprover2).format(DEFAULT_DATE_FORMAT)
                    : '-',
                "Approver 2": row.approver2 || "-",
                "Approver 2 Approval Date": row.approver2ApprovalDate && moment(row.approver2ApprovalDate).isValid()
                    ? moment(row.approver2ApprovalDate).format(DEFAULT_DATE_FORMAT)
                    : '-',
                "Sent To Approver 3": row.sentToApprover3 && moment(row.sentToApprover3).isValid()
                    ? moment(row.sentToApprover3).format(DEFAULT_DATE_FORMAT)
                    : '-',
                "Approver 3": row.approver3 || "-",
                "Approver 3 Approval Date": row.approver3ApprovalDate && moment(row.approver3ApprovalDate).isValid()
                    ? moment(row.approver3ApprovalDate).format(DEFAULT_DATE_FORMAT)
                    : '-',
                "Sent To Approver 4": row.sentToApprover4 && moment(row.sentToApprover4).isValid()
                    ? moment(row.sentToApprover4).format(DEFAULT_DATE_FORMAT)
                    : '-',
                "Approver 4": row.approver4 || "-",
                "Approver 4 Approval Date": row.approver4ApprovalDate && moment(row.approver4ApprovalDate).isValid()
                    ? moment(row.approver4ApprovalDate).format(DEFAULT_DATE_FORMAT)
                    : '-',
                "Sent To Approver 5": row.sentToApprover5 && moment(row.sentToApprover5).isValid()
                    ? moment(row.sentToApprover5).format(DEFAULT_DATE_FORMAT)
                    : '-',
                "Approver 5": row.approver5 || "-",
                "Approver 5 Approval Date": row.approver5ApprovalDate && moment(row.approver5ApprovalDate).isValid()
                    ? moment(row.approver5ApprovalDate).format(DEFAULT_DATE_FORMAT)
                    : '-',
                "Finance Submission Date": row.financeSubmissionDate && moment(row.financeSubmissionDate).isValid()
                    ? moment(row.financeSubmissionDate).format(DEFAULT_DATE_FORMAT)
                    : '-',
                "Payment Date": row.paymentDate && moment(row.paymentDate).isValid()
                    ? moment(row.paymentDate).format(DEFAULT_DATE_FORMAT)
                    : '-',
                "Paid Amount": Number(row.paidAmount).toLocaleString("en-IN") || "-",
                "Status": row.status || "-",
                "Hold Reason / Observations": row.holdReasonObservations || "-",
                "UTR No.": row.utrNo || "-",
                "Invoice Ageing For Approvals": row.invoiceAgeingForApprovals || "-",
                "Invoice Ageing For Payments": row.invoiceAgeingForPayments || "-",
            };
        });

    useEffect(() => {
        trigger(filters);
    }, []);

    useEffect(() => {
        const delayDebounceFn = setTimeout(() => {
            if (searchTerm !== filters.SearchTerm) {
                const updatedFilters = {
                    ...filters,
                    SearchTerm: searchTerm,
                    PageNumber: 1,
                };
                setFilters(updatedFilters);
                trigger(updatedFilters);
                setPageNumber(1);
            }
        }, 500);
        return () => clearTimeout(delayDebounceFn);
    }, [searchTerm]);

    const handlePageChange = (newPage) => {
        setPageNumber(newPage);
        const updatedFilters = {
            ...filters,
            PageNumber: newPage,
        };
        setFilters(updatedFilters);
        trigger(updatedFilters);
    };

    const handlePageSizeChange = (newSize) => {
        setPageSize(newSize);
        setPageNumber(1);
        const updatedFilters = {
            ...filters,
            PageSize: newSize,
            PageNumber: 1,
        };
        setFilters(updatedFilters);
        trigger(updatedFilters);
    };

    const handleApplyFilters = () => {
        const updatedFilters = {
            ...filters,
            PageNumber: 1,
        };

        setFilters(updatedFilters);
        trigger(updatedFilters);
        setPageNumber(1);
        handleClose();
    };

    const handleReset = () => {
        const resetFilters = {
            ProjectId: projectId,
            SortBy: sortBy,
            SortOrder: sortOrder,
            PageNumber: 1,
            PageSize: 10,
            SearchTerm: "",
        };
        setFilters(resetFilters);
        trigger(resetFilters);
        setPageNumber(1);
        handleClose();
    };

    const handleSelectChange = (selectedOption, fieldName) => {
        const updatedFilters = {
            ...filters,
            [fieldName]: selectedOption?.value ?? null,
            PageNumber: 1,
        };

        setFilters(updatedFilters);
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;

        const updatedFilters = {
            ...filters,
            [name]: value ? `${value}T00:00:00Z` : "",
            PageNumber: 1,
        };

        setFilters(updatedFilters);
    };

    const handleRequestSort = (property) => {
        const isAsc = sortBy === property && sortOrder === 'asc';
        const updatedFilters = {
            ...filters,
            SortBy: property,
            SortOrder: isAsc ? 'desc' : 'asc'
        };
        setFilters(updatedFilters);
        setSortOrder(isAsc ? 'desc' : 'asc');
        setSortBy(property);
        trigger(updatedFilters);
    };

    const handleDelete = (filterType) => {
        const updatedFilters = {
            ...filters,
            [filterType]: "",
            PageNumber: 1
        };

        setFilters(updatedFilters);
        trigger(updatedFilters);
        setPageNumber(1);
    };

    const handleAddInvoice = () => {
        setIsAddInvoice((prev) => !prev);
    };

    const handleToggle = () => {
        setOpen((prevOpen) => !prevOpen);
    };

    const handleClose = () => {
        setOpen(false);
    };

    const handleEditInvoice = (row) => {
        setIsEdit((prev) => !prev);
        setSelectedData(row);
    };

    const handleEditInvoiceClose = () => {
        setIsEdit((prev) => !prev);
        setSelectedData(null);
    };

    const handleViewClick = (row) => {
        setIsView((prev) => !prev);
        setViewData(row);
    };

    const handleViewClose = () => {
        setIsView(false);
        setViewData(null);
    };

    const highlightText = (text, highlight) => {
        if (!highlight || !text) return text;

        const regex = new RegExp(`(${highlight})`, 'gi');
        const parts = String(text).split(regex);

        return parts.map((part, index) =>
            regex.test(part) ? (
                <span
                    key={index}
                    style={{
                        backgroundColor: '#FFF3CD',
                        color: '#856404',
                        fontWeight: 'bold',
                        padding: '0 2px',
                        borderRadius: '3px'
                    }}
                >
                    {part}
                </span>
            ) : part
        );
    };

    const columns = [
        {
            name: "",
            field: "actions",
            width: "10px",
            sticky: true,
            renderer: (row) => (
                <>
                    {(viewAccess || editAccess) &&
                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                            <IconButton
                                size="small"
                                onClick={(e) => handleMenuOpen(e, row)}
                            >
                                <MoreVertIcon fontSize="small" />
                            </IconButton>
                        </Box>
                    }
                </>
            ),
        },
        {
            name: "ID",
            field: "invoiceUniqueId",
            width: "100px",
            sticky: true,
        },
        {
            name: "Vendor Name",
            field: "vendorName",
            width: "200px",
            sticky: true,
            renderer: (row) => row?.vendorName ? highlightText(row.vendorName, searchTerm) : "N/A",
        },
        {
            name: "WO/Contract No.",
            field: "woContractNo",
            width: "200px",
            sticky: true,
            renderer: (row) => row?.woContractNo ? highlightText(row.woContractNo, searchTerm) : "N/A",
        },
        {
            name: "Invoice Particulars",
            field: "invoiceParticulars",
            width: "250px",
            renderer: (row) => row?.invoiceParticulars ? highlightText(row.invoiceParticulars, searchTerm) : "N/A",
        },
        {
            name: "Type ",
            field: "type",
            width: "200px",
            renderer: (row) => row?.type ? row?.type : "N/A",
        },
        {
            name: "Invoice No.",
            field: "invoiceNo",
            width: "200px",
            renderer: (row) => row?.invoiceNo ? highlightText(row.invoiceNo, searchTerm) : "N/A",
        },
        {
            name: "Invoice Date",
            field: "invoiceDate",
            renderer: (row) => (
                row?.invoiceDate ? moment.utc(row.invoiceDate).format("DD MMM YYYY") : "N/A"
            ),
        },
        {
            name: "Total Invoice Amount (Inc GST)",
            field: "totalInvoiceAmountIncGst",
            renderer: (row) => {
                const value = row?.totalInvoiceAmountIncGst;

                if (!value) return "N/A";

                return Number(value).toLocaleString("en-IN");
            },
        },
        {
            name: "Received From Vendor",
            field: "receivedFromVendor",
            renderer: (row) => (
                row?.receivedFromVendor ? moment.utc(row.receivedFromVendor).format("DD MMM YYYY") : "N/A"
            ),
        },
        {
            name: "Invoice Received For PMO Review",
            field: "invoiceReceivedForPmoReview",
            renderer: (row) => (
                row?.invoiceReceivedForPmoReview ? moment.utc(row.invoiceReceivedForPmoReview).format("DD MMM YYYY") : "N/A"
            ),
        },
        {
            name: "PMO Review",
            field: "pmoReview",
            renderer: (row) => (
                row?.pmoReview ? moment.utc(row.pmoReview).format("DD MMM YYYY") : "N/A"
            ),
        },
        {
            name: "Comments",
            field: "comments",
            width: "300px",
            renderer: (row) => {
                const comments = row?.comments;

                if (!comments || comments.length === 0) return "N/A";

                const sortedComments = [...comments].sort(
                    (a, b) => new Date(b.createdDate) - new Date(a.createdDate)
                );

                return sortedComments[0]?.comments ? sortedComments[0]?.comments : "N/A";
            }
        },
        {
            name: "PMO Review After Comments Incorporated",
            field: "pmoReviewAfterCommentsIncorporated",
            renderer: (row) => (
                row?.pmoReviewAfterCommentsIncorporated ? moment.utc(row.pmoReviewAfterCommentsIncorporated).format("DD MMM YYYY") : "N/A"
            ),
        },
        {
            name: "Sent To Approver 1",
            field: "sentToApprover1",
            renderer: (row) => (
                row?.sentToApprover1 ? moment.utc(row.sentToApprover1).format("DD MMM YYYY") : "N/A"
            ),
        },
        {
            name: "Approver 1",
            field: "approver1",
            renderer: (row) => row?.approver1 ? row?.approver1 : "N/A",
        },
        {
            name: "Approver 1 Approval Date",
            field: "approver1ApprovalDate",
            renderer: (row) => (
                row?.approver1ApprovalDate ? moment.utc(row.approver1ApprovalDate).format("DD MMM YYYY") : "N/A"
            ),
        },
        {
            name: "Sent To Approver 2",
            field: "sentToApprover2",
            renderer: (row) => (
                row?.sentToApprover2 ? moment.utc(row.sentToApprover2).format("DD MMM YYYY") : "N/A"
            ),
        },
        {
            name: "Approver 2",
            field: "approver2",
            renderer: (row) => row?.approver2 ? row?.approver2 : "N/A",
        },
        {
            name: "Approver 2 Approval Date",
            field: "approver2ApprovalDate",
            renderer: (row) => (
                row?.approver2ApprovalDate ? moment.utc(row.approver2ApprovalDate).format("DD MMM YYYY") : "N/A"
            ),
        },
        {
            name: "Sent To Approver 3",
            field: "sentToApprover3",
            renderer: (row) => (
                row?.sentToApprover3 ? moment.utc(row.sentToApprover3).format("DD MMM YYYY") : "N/A"
            ),
        },
        {
            name: "Approver 3",
            field: "approver3",
            renderer: (row) => row?.approver3 ? row?.approver3 : "N/A",
        },
        {
            name: "Approver 3 Approval Date",
            field: "approver3ApprovalDate",
            renderer: (row) => (
                row?.approver3ApprovalDate ? moment.utc(row.approver3ApprovalDate).format("DD MMM YYYY") : "N/A"
            ),
        },
        {
            name: "Sent To Approver 4",
            field: "sentToApprover4",
            renderer: (row) => (
                row?.sentToApprover4 ? moment.utc(row.sentToApprover4).format("DD MMM YYYY") : "N/A"
            ),
        },
        {
            name: "Approver 4",
            field: "approver4",
            renderer: (row) => row?.approver4 ? row?.approver4 : "N/A",
        },
        {
            name: "Approver 4 Approval Date",
            field: "approver4ApprovalDate",
            renderer: (row) => (
                row?.approver4ApprovalDate ? moment.utc(row.approver4ApprovalDate).format("DD MMM YYYY") : "N/A"
            ),
        },
        {
            name: "Sent To Approver 5",
            field: "sentToApprover5",
            renderer: (row) => (
                row?.sentToApprover5 ? moment.utc(row.sentToApprover5).format("DD MMM YYYY") : "N/A"
            ),
        },
        {
            name: "Approver 5",
            field: "approver5",
            renderer: (row) => row?.approver5 ? row?.approver5 : "N/A",
        },
        {
            name: "Approver 5 Approval Date",
            field: "approver5ApprovalDate",
            renderer: (row) => (
                row?.approver5ApprovalDate ? moment.utc(row.approver5ApprovalDate).format("DD MMM YYYY") : "N/A"
            ),
        },
        {
            name: "Finance Submission Date",
            field: "financeSubmissionDate",
            renderer: (row) => (
                row?.financeSubmissionDate ? moment.utc(row.financeSubmissionDate).format("DD MMM YYYY") : "N/A"
            ),
        },
        {
            name: "Payment Date",
            field: "paymentDate",
            renderer: (row) => (
                row?.paymentDate ? moment.utc(row.paymentDate).format("DD MMM YYYY") : "N/A"
            ),
        },
        {
            name: "Paid Amount",
            field: "paidAmount",
            renderer: (row) => {
                const value = row?.paidAmount;

                if (!value) return "N/A";

                return Number(value).toLocaleString("en-IN");
            },
        },
        {
            name: "Status",
            field: "status",
            renderer: (row) => (
                row?.status ? <StatusBadge status={row?.status} /> : "N/A"
            )
        },
        {
            name: "Hold Reason / Observations",
            field: "holdReasonObservations",
            width: "300px",
            renderer: (row) => row?.holdReasonObservations ? row?.holdReasonObservations : "N/A",
        },
        {
            name: "UTR No.",
            field: "utrNo",
            renderer: (row) => row?.utrNo ? row?.utrNo : "N/A",
        },
        {
            name: "Invoice Ageing For Approvals",
            field: "invoiceAgeingForApprovals",
            renderer: (row) => row?.invoiceAgeingForApprovals ?? "N/A",
        },
        {
            name: "Invoice Ageing For Payments",
            field: "invoiceAgeingForPayments",
            renderer: (row) => row?.invoiceAgeingForPayments ?? "N/A",
        }
    ];

    return (
        <>
            <TrackerPageHeader title="Invoice Tracker" description="Track invoices, vendors, approvals, and payment status.">
                <ProjectLastUpdatedOn date={invoiceData?.invoiceApproval} />
            </TrackerPageHeader>
            <Grid container spacing={2} sx={{ p: 2 }}>

                <Grid size={12}>
                    <Box
                        sx={{
                            width: "100%",
                            borderBottom: "2px solid #ECECEC",
                            overflowX: "auto",
                            whiteSpace: "nowrap",
                            p: 0,
                            display: "flex", alignItems: "center", justifyContent: 'space-between',
                            "-webkit-overflow-scrolling": "touch",
                        }}
                    >
                        <Tabs
                            value={tabValue}
                            //onChange={handleTabChange}
                            TabIndicatorProps={{ style: { display: "none" } }}
                            variant="scrollable"
                            scrollButtons={false}
                            sx={{
                                justifyContent: "flex-start !important",
                                alignItems: "flex-start",
                                "& .MuiTabs-flexContainer": {
                                    justifyContent: "flex-start !important",
                                    gap: "30px",
                                },
                            }}
                        >
                            {["All"].map((label, index) => {
                                let count = 0;

                                if (label === "All") {
                                    count = totalCount;
                                }
                                else if (label === "Open") {
                                    count = openCount;
                                }
                                else if (label === "Closed") {
                                    count = closedCount;
                                }

                                return (
                                    <Tab
                                        key={index}
                                        label={
                                            <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
                                                <Typography
                                                    sx={{
                                                        fontWeight: 500,
                                                        fontSize: "14px",
                                                        lineHeight: "100%",
                                                        color: tabValue === index ? "#0E73F6" : "#7E7E7E",
                                                        fontFamily: "Outfit, sans-serif"
                                                    }}
                                                >
                                                    {label}
                                                </Typography>
                                                {index > 0 &&
                                                    <Chip
                                                        label={count}
                                                        sx={{
                                                            height: "20px",
                                                            fontSize: "14px",
                                                            fontWeight: 500,
                                                            lineHeight: 1,
                                                            backgroundColor: "#E5E9EB",
                                                            color: "#5F5F5F",
                                                        }}
                                                    />
                                                }

                                            </Box>
                                        }
                                        sx={{
                                            textTransform: "none",
                                            minWidth: "auto",
                                            paddingBottom: "14px",
                                            paddingLeft: 0,
                                            paddingRight: 0,
                                            borderBottom: tabValue === index ? "2px solid #0E73F6" : "2px solid transparent",
                                        }}
                                    />
                                );
                            })}
                        </Tabs>
                        {createAccess &&
                            <CustomButton
                                label="Create Invoice"
                                startIcon={<FaPlus />}
                                color="primary"
                                onClick={handleAddInvoice}
                                sx={{
                                    backgroundColor: "#2563EB",
                                    fontFamily: 'Outfit',
                                    fontWeight: 500,
                                    fontSize: '14px',
                                    lineHeight: '100%',
                                    letterSpacing: '0px',
                                }}
                            />
                        }
                    </Box>
                </Grid>

                <Grid size={12}>
                    <Grid container>
                        <Grid size={{ md: 8, sm: 6 }}>
                            <Stack
                                direction="row"
                                spacing={1}
                                flexWrap="wrap"
                                sx={{
                                    padding: "8px 0",
                                    alignItems: "center",
                                    minHeight: "40px"
                                }}
                            >

                                {filters?.VendorName &&
                                    <Chip
                                        label={
                                            <Typography variant="body2">
                                                <strong style={{ color: '#0E73F6' }}>Vendor Name:</strong>{" "}
                                                <span style={{ fontWeight: 400, color: "#6B7280" }}>{filters?.VendorName}</span>
                                            </Typography>
                                        }
                                        sx={{
                                            backgroundColor: "rgba(25, 118, 210, 0.08)",
                                            border: 0
                                        }}
                                        variant="outlined"
                                        onDelete={() => handleDelete('VendorName')}
                                    />
                                }

                                {filters.InvoiceDate && (
                                    <Chip
                                        label={
                                            <Typography variant="body2">
                                                <strong style={{ color: '#0E73F6' }}>Invoice Date:</strong>{" "}
                                                <span style={{ fontWeight: 400, color: "#6B7280" }}>
                                                    {moment(filters.InvoiceDate).format('DD-MM-YYYY')}
                                                </span>
                                            </Typography>
                                        }
                                        sx={{
                                            backgroundColor: "rgba(25, 118, 210, 0.08)",
                                            border: 0
                                        }}
                                        variant="outlined"
                                        onDelete={() => handleDelete('InvoiceDate')}
                                    />
                                )}

                                {filters.Status && (
                                    <Chip
                                        label={
                                            <Typography variant="body2">
                                                <strong style={{ color: '#0E73F6' }}>Status:</strong>{" "}
                                                <span style={{ fontWeight: 400, color: "#6B7280" }}>
                                                    {filters.Status}
                                                </span>
                                            </Typography>
                                        }
                                        sx={{
                                            backgroundColor: "rgba(25, 118, 210, 0.08)",
                                            border: 0
                                        }}
                                        variant="outlined"
                                        onDelete={() => handleDelete('Status')}
                                    />
                                )}

                            </Stack>
                        </Grid>


                        <Grid size={{ md: 4, sm: 6 }}>
                            <Box
                                sx={{
                                    display: "flex",
                                    flexDirection: { md: "row", xs: "column" },
                                    alignItems: { md: "center", xs: "flex-start" },
                                    justifyContent: 'end',
                                    p: "8px 0px"
                                }}
                            >
                                <TextField
                                    placeholder="Search By Vendor Name, Invoice No., Status ..."
                                    value={searchTerm}
                                    onChange={handleSearchChange}
                                    variant="outlined"
                                    size="small"
                                    InputProps={{
                                        startAdornment: <LuSearch style={{ marginRight: '8px', color: '#6B7280' }} />,
                                    }}
                                    sx={{
                                        minWidth: { md: '320px', xs: "auto" },
                                        // width: "100%",
                                        backgroundColor: '#fff',
                                        '& .MuiOutlinedInput-root': { borderRadius: '3px', minHeight: '36px', fontSize: '13px' },
                                        '& .MuiInputBase-input::placeholder': {
                                            fontSize: '0.9rem',
                                        }
                                    }}
                                    type="search"
                                />

                                <IconButton
                                    ref={anchorRef}
                                    onClick={handleToggle}
                                >
                                    <MdOutlineFilterAlt size={24} color="#48535B" />
                                </IconButton>

                                <Popper
                                    open={open}
                                    anchorEl={anchorRef.current}
                                    placement="bottom-end"
                                    transition
                                    disablePortal={false}
                                    sx={{ zIndex: 2000 }}
                                >
                                    {({ TransitionProps }) => (
                                        <Grow {...TransitionProps}>
                                            <Paper
                                                sx={{
                                                    mt: 1,
                                                    p: 2,
                                                    borderRadius: 1,
                                                    boxShadow: "0px 4px 20px rgba(7, 7, 7, 0.08)",
                                                    width: 600,
                                                }}
                                            >
                                                <ClickAwayListener onClickAway={handleClose}>
                                                    <Box>
                                                        <Grid container spacing={2}>
                                                            <Grid size={{ xs: 12, md: 6 }}>
                                                                <Typography sx={{
                                                                    fontFamily: 'Outfit',
                                                                    fontWeight: 500,
                                                                    fontSize: '16px',
                                                                    lineHeight: '100%',
                                                                    letterSpacing: "0px",
                                                                    mb: 1
                                                                }}>Select Status</Typography>
                                                                <Select
                                                                    name="Status"
                                                                    options={statusOptions}
                                                                    isClearable
                                                                    placeholder="Select Status"
                                                                    onChange={(option) => handleSelectChange(option, "Status")}
                                                                    value={statusOptions.find(option => option.value === filters.Status) || null}
                                                                />
                                                            </Grid>

                                                            <Grid size={{ xs: 12, md: 6 }}>
                                                                <Typography sx={{
                                                                    fontFamily: 'Outfit',
                                                                    fontWeight: 500,
                                                                    fontSize: '16px',
                                                                    lineHeight: '100%',
                                                                    letterSpacing: "0px",
                                                                    mb: 1
                                                                }}>Select Vendor Name</Typography>
                                                                <Select
                                                                    name="VendorName"
                                                                    options={vendorNameOptions}
                                                                    isClearable
                                                                    placeholder="Select Vendor Name"
                                                                    onChange={(option) => handleSelectChange(option, "VendorName")}
                                                                    value={vendorNameOptions.find(option => option.value === filters.VendorName) || null}
                                                                />
                                                            </Grid>

                                                            <Grid size={{ xs: 12, md: 6 }}>
                                                                <Typography sx={{
                                                                    fontFamily: 'Outfit',
                                                                    fontWeight: 500,
                                                                    fontSize: '16px',
                                                                    lineHeight: '100%',
                                                                    letterSpacing: "0px",
                                                                    mb: 1
                                                                }}>Invoice Date</Typography>
                                                                <TextField
                                                                    type="date"
                                                                    name="InvoiceDate"
                                                                    // onBlur={handleBlur}
                                                                    value={filters.InvoiceDate ? new Date(filters.InvoiceDate).toISOString().split('T')[0] : ""}
                                                                    onChange={handleInputChange}
                                                                    onClick={(e) => {
                                                                        // Trigger the native date picker
                                                                        const input = e.currentTarget.parentElement?.querySelector('input[type="date"]');
                                                                        if (input) input.showPicker?.();
                                                                    }}
                                                                    fullWidth
                                                                    sx={{
                                                                        // ...rightTextStyle,
                                                                        "& input::-webkit-calendar-picker-indicator": {
                                                                            display: "none",
                                                                            WebkitAppearance: "none",
                                                                        },
                                                                        "& input[type='date']": {
                                                                            appearance: "none",
                                                                            WebkitAppearance: "none",
                                                                        },
                                                                        "& .MuiOutlinedInput-root": {
                                                                            "& fieldset": {
                                                                                border: "none",
                                                                            },
                                                                        },
                                                                    }}
                                                                    InputProps={{
                                                                        startAdornment: (
                                                                            <Box sx={{ ml: 2, mr: 1, color: "#433F5E", display: "flex", alignItems: "center", cursor: "pointer" }}

                                                                            >
                                                                                {CalenderSVG}
                                                                            </Box>
                                                                        ),
                                                                        sx: {
                                                                            backgroundColor: "#E7E7EB",
                                                                            height: "40px",
                                                                            width: { xs: "100%", md: "100%" },
                                                                            borderRadius: "3px",
                                                                        },
                                                                    }}
                                                                />
                                                            </Grid>

                                                            <Grid size={12}>
                                                                <Box
                                                                    sx={{
                                                                        display: "flex",
                                                                        justifyContent: "flex-end",
                                                                        alignItems: "center",
                                                                    }}
                                                                >
                                                                    <Button onClick={handleReset}
                                                                        sx={{
                                                                            textTransform: "none",
                                                                            color: "#F15744",
                                                                            fontSize: "14px",
                                                                            lineHeight: "100%",
                                                                            fontFamily: "Outfit, sans-serif",
                                                                            borderRadius: "3px",
                                                                            fontWeight: 500,
                                                                            px: "20px",
                                                                            py: "15px",
                                                                        }}>
                                                                        Reset
                                                                    </Button>
                                                                    <Button
                                                                        type="button"
                                                                        onClick={handleApplyFilters}
                                                                        variant="contained"
                                                                        sx={{
                                                                            backgroundColor: "#2563EB",
                                                                            borderRadius: "3px",
                                                                            textTransform: "none",
                                                                            fontWeight: 500,
                                                                            fontSize: "14px",
                                                                            lineHeight: "100%",
                                                                            fontFamily: "Outfit, sans-serif",
                                                                            color: "#FFFFFF",
                                                                            px: "20px",
                                                                            py: "15px",
                                                                        }}
                                                                    >
                                                                        Apply
                                                                    </Button>
                                                                </Box>
                                                            </Grid>
                                                        </Grid>
                                                    </Box>
                                                </ClickAwayListener>
                                            </Paper>
                                        </Grow>
                                    )}
                                </Popper>

                                <IconButton onClick={() =>
                                    handleExport(
                                        filters,
                                        exportInvoiceData
                                    )
                                } disabled={isExporting}>
                                    <MdOutlineFileDownload size={24} color="#48535B" />
                                </IconButton>

                                <IconButton onClick={() => {
                                    trigger(filters);
                                }}>
                                    <LuRefreshCw size={20} color="#48535B" />
                                </IconButton>

                            </Box>
                        </Grid>
                    </Grid>
                </Grid>

                <Grid size={12}>
                    <TableComponent
                        data={invoiceList?.data || []}
                        isLoading={isFetching}
                        columns={columns}
                        order={sortOrder}
                        orderBy={sortBy}
                        createSortHandler={handleRequestSort}
                    />
                    <Menu
                        anchorEl={menuAnchorEl}
                        open={Boolean(menuAnchorEl)}
                        onClose={handleMenuClose}
                        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                        transformOrigin={{ vertical: "top", horizontal: "right" }}
                        PaperProps={{
                            elevation: 0,
                            sx: {
                                mt: 1,
                                borderRadius: "3px",
                                minWidth: 140,
                                border: "1px solid #E5E9EB",
                                boxShadow: "0px 10px 30px rgba(15, 23, 42, 0.12)",
                                overflow: "hidden",
                            },
                        }}
                        MenuListProps={{
                            sx: {
                                py: 0.5,
                            },
                        }}
                    >
                        {viewAccess &&
                            <MenuItem
                                onClick={handleMenuView}
                                sx={{
                                    fontFamily: "Outfit, sans-serif",
                                    fontSize: "14px",
                                    fontWeight: 400,
                                    px: 1.5,
                                    py: 1,
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                    color: "#111827",
                                    "&:hover": {
                                        backgroundColor: "#F6F8F9",
                                    },
                                }}
                            >
                                <LuEye size={16} style={{ color: "#4B5563" }} />
                                <span>View</span>
                            </MenuItem>
                        }

                        {editAccess && <MenuItem
                            onClick={handleMenuEdit}
                            sx={{
                                fontFamily: "Outfit, sans-serif",
                                fontSize: "14px",
                                fontWeight: 400,
                                px: 1.5,
                                py: 1,
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                                color: "#111827",
                                "&:hover": {
                                    backgroundColor: "#F6F8F9",
                                },
                            }}
                        >
                            <EditIcon style={{ width: 14, height: 14 }} />
                            <span>Edit</span>
                        </MenuItem>
                        }
                    </Menu>

                    <CustomPagination
                        totalCount={invoiceList?.totalRecords || 0}
                        pageSize={pageSize}
                        page={pageNumber}
                        onPageChange={handlePageChange}
                        setCurrentPage={setPageNumber}
                        onPageSizeChange={handlePageSizeChange}
                    />

                </Grid>
            </Grid >

            {isAddInvoice && <AddInvoice open={isAddInvoice} handleClose={handleAddInvoice} vendorNameOptions={vendorNameOptions} statusOptions={statusOptions} />}
            {isEdit && <EditInvoice open={isEdit} handleClose={handleEditInvoiceClose} rowData={selectedData} vendorNameOptions={vendorNameOptions} statusOptions={statusOptions} />}
            {isView && <ViewInvoice open={isView} handleClose={handleViewClose} rowData={viewData} />}
        </>
    );
}

export default InvoiceTracker;
